import {Grid, Typography} from '@mui/material';
import {useSelector, useDispatch} from 'react-redux';
import {
  GridApiPro,
  GridFilterItem,
  GridFilterModel,
  GridLocaleText,
  GridRenderCellParams,
  GridRenderHeaderFilterProps,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
  MuiEvent,
} from '@mui/x-data-grid-pro';
import {t} from 'i18next';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  MutableRefObject,
} from 'react';
import {useNavigate} from 'react-router-dom';
import _, {debounce} from 'lodash';
import {RootState} from '../../../../redux/store';
import {ReactComponent as InfoAlertIcon} from '../../../../assests/svg/info-outlined-blue.svg';
import {ReactComponent as InfoIcon} from '../../../../assests/svg/info_icon.svg';
import {
  colorArray,
  TABLE_PAGE_SIZE_OPTION,
  TABLE_PER_PAGE_DEFAULT_NO_ROW,
  TaggingStatus,
  TOAST_TIMEOUT,
  ToastType,
} from '../../../../constants/constants';
import navConstants from '../../../../constants/navConstants';
import {useToast} from '../../../../contexts/toastContext/ToastContext';
import {
  setCurrentFieldTemplateId,
  setStrReportId,
} from '../../../../redux/slices/strSlice';
import {
  formateNumber,
  getNextPageAfterDelete,
  getUTCDateTimeForDisplay,
  unwrapQueryText,
  wrapQueryBeforeSymbol,
} from '../../../../utils/utils';
import AurCheckbox from '../../../common/aur-checkbox/AurCheckbox';
import AurDataGridPro, {
  CustomGridColDef,
} from '../../../common/aur-data-grid-pro/AurDataGridPro';
import AurDialog from '../../../common/aur-dialog/AurDialog';
import {IconColors, IconNames} from '../../../common/aur-icon/AurIcon';
import AurPagination from '../../../common/aur-pagination/AurPagination';
import AurTooltip from '../../../common/aur-tooltip/AurTooltip';
import {STR_FLOW} from '../../search-term-report/CreateSTR';
import {STRResultTermListType} from '../ViewSTR';
import {DEFAULT_TEXT_COLOR} from '../../../../utils/createSTRUtils';
import {
  getParseSTRStatus,
  useSTRTermStatusList,
  getSTRStatusString,
  STRStatusType,
} from '../hook/useSTRStatus';
import FilterTextField from '../../../common/aur-data-grid-pro/FilterTextField';
import FieldPopoverGridFilter from '../../field-management/field-table/FieldPopoverGridFilter';
import {
  getOperatorFilterValue,
  toODataQuery,
} from '../../../common/aur-data-grid-pro/useServerSideDataGrid';
import AurEcaSearchLoader from '../../../common/aur-eca-search-loader/AurEcaSearchLoader';
import EcaDocumentContainer from '../../../eca-doc-list/eca-document-viewer/EcaDocumentContainer';
import {
  setAllGridDocuments,
  setEcaDocumentViewerModal,
  setStrSearchFlag,
  setTruncatedBannerText,
  setNativePath,
  setExtractedTextPath,
  setShowHighLightedTab,
} from '../../../../redux/slices/ecaDocumentViewerSlice';
import {IFilterSet} from '../../../eca-doc-list/eca-doc-list.util';
import {defaultFilterSet} from '../../../eca-doc-list/EcaDocListGrid';
import {getSTRDocList} from '../../../../api/EcaServices';
import {getHighlightedDocumentSearchText} from '../../../../api/EcaManagementService';
import {setHighlights} from '../../../../redux/slices/ecaDocumentHighLightPanelSlice';
import {Highlight} from '../../../../components/eca-doc-list/eca-document-viewer/highlight-panel/EcaHighLightedPanel';
import {useBlockElementScroll} from '../hook/useBlockElementScroll';
import {setTaggingStatus} from '../../../../redux/slices/ecaDocListSlice';
import STRIconButton from './STRIconButton';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../../hooks/permissions/usePermissionsByPage';
import {
  GA_EVENT_NAMES,
  GA_CLICK_TYPES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';
import {useAnalytics} from '../../../../hooks/useAnalytics';

const stringPath = 'eca.searchTermReport.viewSTR.termTable.';
const customLocaleText = {
  headerFilterOperatorNotSet: 'Not Set',
} as Partial<GridLocaleText> & Record<string, string>;

export interface Props {
  status: STRStatusType | null;
  reportName?: string | null;
  filteredTerms: STRResultTermListType[];
  allTerms: STRResultTermListType[];
  haveError: boolean;
  isIncludeUniqueHitsFlagColumnVisible?: boolean;
  isTermUniqueHitsWithRGFColumnVisible?: boolean;
  isDocsHitsWithRGFColumnVisible?: boolean;
  onErrorRefresh: () => void;
  onDeleteSelectedTerm: (terms: string[], onSuccess?: () => void) => void;
  onODataQueryApply: (query: string | null) => void;
  totalTerms: number;
  reportId: string;
  fieldTemplateId: string;
  onTaggingSelectedTerm: () => void;
  onTermsSelection: (terms: string[]) => void;
  gridAPIRef: MutableRefObject<GridApiPro>;
}

interface PaginationConfigType {
  currentPage: number;
  itemPerPage: number;
}

type SelectedTermData = {
  status: string;
  termDocHits: number;
};

type FilterPaginationConfigType = {
  paginationConfig: PaginationConfigType;
  filter: GridFilterModel | null;
  sort: GridSortModel;
};

const STRSearchTermTable: React.FC<Props> = ({
  status,
  reportName,
  filteredTerms,
  allTerms,
  haveError,
  isIncludeUniqueHitsFlagColumnVisible = false,
  isTermUniqueHitsWithRGFColumnVisible = false,
  isDocsHitsWithRGFColumnVisible = false,
  onErrorRefresh,
  onDeleteSelectedTerm,
  totalTerms,
  reportId,
  fieldTemplateId,
  onODataQueryApply,
  onTaggingSelectedTerm,
  onTermsSelection,
  gridAPIRef,
}) => {
  const navigate = useNavigate();
  const {showToast} = useToast();
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();
  const termStatusList = useSTRTermStatusList();
  const [selectedTermIds, setSelectedTermIds] = useState<
    Map<string, SelectedTermData>
  >(new Map());
  const [showSearchLoader, setShowSearchLoader] = useState<boolean>(false);
  const selectionDocuments = useRef<string[]>([]);
  const [selectedDocLink] = useState<string>('');
  const [scrollFetching] = useState<boolean>(true);
  const [filterSet] = useState<IFilterSet>(defaultFilterSet);
  const [startIndex] = useState(1);
  const [itemsPerPage] = useState(200);

  const {hasSearchesPerm} = usePermissionsByPage(PAGE_NAME.ECA_OVERVIEW);

  const [filterPaginationConfig, setFilterPaginationConfig] =
    useState<FilterPaginationConfigType>({
      paginationConfig: {
        currentPage: 1,
        itemPerPage: TABLE_PER_PAGE_DEFAULT_NO_ROW,
      },
      filter: null,
      sort: [],
    });

  const ecaDocumentViewer = useSelector(
    (state: RootState) => state.ecaDocumentViewer.ecaDocumentViewerPopup
  );

  const projectCode = useSelector(
    (state: RootState) => state.reports.projectAlphaCode
  );
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useBlockElementScroll(scrollRef);
  const clientId = useSelector((state: RootState) => state.reports.clientId);

  const projectId = useSelector((state: RootState) => state.reports.projectId);

  const {hasDocumentPreviewPerm, hasTaggingDocumentPerm} = usePermissionsByPage(
    PAGE_NAME.STR_SEARCH_TERM
  );

  const [confirmDeleteDialogVisible, setConfirmDeleteDialogVisible] =
    useState<boolean>(false);
  const toolTipStringPath = 'eca.searchTermReport.viewSTR.toolTipText.';
  const debounceSetFilterValue = useMemo(
    () =>
      debounce((val: FilterPaginationConfigType) => {
        gridAPIRef.current.setRowSelectionModel([]);
        setFilterPaginationConfig(val);
      }, 500), // 500ms delay
    []
  );

  const taggingStatus = useSelector(
    (state: RootState) => state.ecaDocList.taggingStatus
  );

  useEffect(() => {
    if (taggingStatus) {
      const toastType =
        taggingStatus === TaggingStatus.Completed
          ? ToastType.Success
          : ToastType.Error;
      showToast(
        t(
          `ecaDocList.bulkTagging.apply.${taggingStatus === TaggingStatus.Completed ? ToastType.Success : ToastType.Failed}`
        ),
        toastType,
        TOAST_TIMEOUT,
        'center',
        'bottom'
      );
      dispatch(setTaggingStatus(null));
    }
  }, [taggingStatus]);

  const isDocCountZeroOrStatusNotCompleted = (
    selectedTermIds: Map<string, SelectedTermData>
  ) => {
    let allDocHitsZero = true;

    for (const data of selectedTermIds.values()) {
      // If any term is NOT completed → disable
      if (getParseSTRStatus(data.status) !== STRStatusType.Completed) {
        return true;
      }
      // Track non-zero doc hits
      if (data.termDocHits !== 0) {
        allDocHitsZero = false;
      }
    }
    // If ALL doc hits are zero → disable
    return allDocHitsZero;
  };

  const disabledTaggingButton = useMemo(() => {
    if (selectedTermIds.size === 0) {
      return true;
    }

    return isDocCountZeroOrStatusNotCompleted(selectedTermIds);
  }, [selectedTermIds]);

  const disabledDocButton = useMemo(() => {
    if (selectedTermIds.size === 0 || status === STRStatusType.InProgress) {
      return true;
    }

    return isDocCountZeroOrStatusNotCompleted(selectedTermIds);
  }, [selectedTermIds, status]);

  const isAllVisibleTermSelected = useMemo(() => {
    if (filteredTerms.length === 0) return false;
    return filteredTerms.every((term) => selectedTermIds.has(term.termId));
  }, [selectedTermIds, filteredTerms]);

  const isAnyVisibleTermSelected = useMemo(() => {
    return filteredTerms.some((term) => selectedTermIds.has(term.termId));
  }, [selectedTermIds, filteredTerms]);

  const fetchHighlightedDocText = async (docId: string) => {
    const params = `?clientId=${clientId}&projectId=${projectId}`;
    return await getHighlightedDocumentSearchText(docId, params, [
      ...selectedTermIds.keys(),
    ]).then((result) => {
      const highlightTerms: Highlight[] = result.data.data.highlights;

      if (Array.isArray(highlightTerms)) {
        const highlights: Highlight[] = highlightTerms.map((h: Highlight) => ({
          term: h.term,
          color: h.color,
        }));
        dispatch(setHighlights(highlights));
        dispatch(
          setExtractedTextPath(
            result.data.data?.extractedTextInfo.extractedTextPath
          )
        );
        dispatch(setNativePath(result.data.data?.extractedTextInfo.nativePath));
        dispatch(
          setTruncatedBannerText(result.data.data?.extractedTextInfo.truncated)
        );
      }
      const formattedText = result.data.data.extractedTextInfo.extractedText
        .replace(/\r\n/g, '<br>')
        .replace(/\u00A0/g, '&nbsp;');
      return formattedText;
    });
  };

  useEffect(() => {
    // any single search term is failed to process and currrent status is InProgress
    if (status === STRStatusType.InProgress) {
      const failedTerm = filteredTerms.find(
        (item) => getParseSTRStatus(item.termRunstatus) === STRStatusType.Failed
      );

      if (failedTerm) {
        showToast(
          t('eca.searchTermReport.viewSTR.failedMsg', {name: reportName}),
          'error',
          undefined,
          'center'
        );
      }
    }
  }, [status]);

  useEffect(() => {
    const {filter, sort, paginationConfig} = filterPaginationConfig;
    const ODataQuery = toODataQuery(
      filter ?? {items: []},
      sort,
      {
        page: paginationConfig.currentPage,
        pageSize: paginationConfig.itemPerPage,
      },
      {
        termRunstatus: (item: GridFilterItem) => {
          return `${item.field} in [${item.value
            .map((v: string) => {
              return `'${v}'`;
            })
            .join(', ')}]`;
        },
        term: (item: GridFilterItem) => {
          return `contains(tolower(${item.field}), tolower('${encodeURIComponent(wrapQueryBeforeSymbol(item.value))}'))`;
        },
        termLastRunTime: (item: GridFilterItem) => {
          if (item.operator === 'NotSet') {
            return `termLastRunTime eq null`;
          }
          return `termLastRunTime ne null and ${getOperatorFilterValue(item)}`;
        },
        termLastUpdatedTime: (item: GridFilterItem) => {
          if (item.operator === 'NotSet') {
            return `termLastUpdatedTime eq null`;
          }
          return `termLastUpdatedTime ne null and ${getOperatorFilterValue(item)}`;
        },
      }
    );
    onODataQueryApply(ODataQuery.substring(1));
  }, [filterPaginationConfig]);

  useEffect(() => {
    return () => {
      debounceSetFilterValue.cancel();
    };
  }, [debounceSetFilterValue]);

  const handleItemsPerPageChange = useCallback((number: number) => {
    gridAPIRef.current.setPage(0);
    gridAPIRef.current.setPageSize(number);
    setFilterPaginationConfig((prevState) => ({
      ...prevState,
      paginationConfig: {
        itemPerPage: number,
        currentPage: 1,
      },
    }));
  }, []);

  const handleCurrenPageChange = useCallback((number: number) => {
    gridAPIRef.current.setPage(number - 1);
    setFilterPaginationConfig((prevState) => ({
      ...prevState,
      paginationConfig: {
        ...prevState.paginationConfig,
        currentPage: number,
      },
    }));
  }, []);

  const handleFilterChangeEvent = useCallback(
    (data: GridFilterModel) => {
      // added check because filter event call when user do horizontal scroll but actually filter not changed
      if (_.isEqual(data, filterPaginationConfig.filter)) {
        return;
      }
      gridAPIRef.current.setPage(0);
      debounceSetFilterValue({
        filter: data,
        sort: filterPaginationConfig.sort,
        paginationConfig: {
          ...filterPaginationConfig.paginationConfig,
          currentPage: 1,
        },
      });
    },
    [filterPaginationConfig]
  );

  const handleSortingChangeEvent = useCallback(
    (sortModel: GridSortModel) => {
      const filteredSortModel = sortModel?.filter((model) => model.sort);
      setFilterPaginationConfig((prevState) => ({
        ...prevState,
        sort: filteredSortModel,
      }));
    },
    [filterPaginationConfig]
  );

  const handleCheckboxCellClick = useCallback(
    (id: string, status: string, termDocHits: number) => {
      const selectedIds: Map<string, SelectedTermData> = new Map(
        selectedTermIds
      );
      if (selectedIds.has(id)) {
        selectedIds.delete(id);
      } else {
        selectedIds.set(id, {status, termDocHits});
      }
      onTermsSelection([...selectedIds.keys()]);
      gridAPIRef.current.setRowSelectionModel([...selectedIds.keys()]);
      setSelectedTermIds(selectedIds);
    },
    [selectedTermIds, onTermsSelection]
  );

  const handleTermRowClick = useCallback(
    (param: GridRowParams, event?: MuiEvent<React.MouseEvent>) => {
      const target = event?.target as HTMLElement;

      // Check if click originated from a checkbox or its wrapper
      const isCheckboxClicked =
        target.closest('input[type="checkbox"]') ||
        target.closest('.MuiCheckbox-root');

      if (isCheckboxClicked) {
        return; // Skip row click event has its trigger due to checkbox click
      }
      const {id, row} = param;
      handleCheckboxCellClick(id as string, row.termRunstatus, row.termDocHits);
    },
    [selectedTermIds]
  );

  const handleAddMoreTermClick = useCallback(() => {
    dispatch(setCurrentFieldTemplateId(parseInt(fieldTemplateId)));
    dispatch(setStrReportId(reportId));
    navigate(navConstants.str.baseSTR + `?Flow=${STR_FLOW.ADDTERMS}`);
  }, [fieldTemplateId, reportId]);

  const handleLoaderClose = () => {
    setShowSearchLoader(false);
  };

  const handleTagginClick = () => {
    onTaggingSelectedTerm();
  };

  const handleViewDocViewerClick = async () => {
    dispatch(setStrSearchFlag(true));
    try {
      const pagination = {
        searchTerm: '',
        sortColumn: '',
        sortOrder: '',
        pageNumber: '1',
        pageSize: '100',
      };
      const response = await getSTRDocList(projectCode as string, reportId, {
        termIds: [...selectedTermIds.keys()],
        paginationQuery: pagination,
      });
      const docIds: string[] = response.data.data.documentIds;
      selectionDocuments.current = docIds;
      const allGridDocs = docIds.map((doc: string) => {
        return {id: doc, documentid: doc};
      });
      dispatch(setAllGridDocuments(allGridDocs));

      dispatch(setEcaDocumentViewerModal(true));
      dispatch(setShowHighLightedTab(true));
    } catch (e) {
      console.error(e);
    }
  };

  const handleConfirmDeleteTermClick = useCallback(() => {
    setConfirmDeleteDialogVisible(false);
    const {currentPage, itemPerPage} = filterPaginationConfig.paginationConfig;
    const nextPageAfterDelete = getNextPageAfterDelete({
      totalItemsBeforeDelete: totalTerms,
      currentPage: currentPage,
      pageSize: itemPerPage,
      numberOfDeletedItems: selectedTermIds.size,
    });
    if (nextPageAfterDelete !== currentPage) {
      onDeleteSelectedTerm([...selectedTermIds.keys()], () => {
        setFilterPaginationConfig((prevState) => ({
          ...prevState,
          paginationConfig: {
            ...prevState.paginationConfig,
            currentPage: nextPageAfterDelete,
          },
        }));
      });
    } else onDeleteSelectedTerm([...selectedTermIds.keys()]);
  }, [selectedTermIds, filterPaginationConfig.paginationConfig, totalTerms]);

  const renderIdHeader = () => {
    const handleSelectAll = () => {
      const selectedIds = new Map(selectedTermIds);
      if (isAllVisibleTermSelected) {
        // Deselect all
        allTerms.forEach((term) => selectedIds.delete(term.termId));
      } else {
        // Select all
        allTerms.forEach((term) =>
          selectedIds.set(term.termId, {
            status: term.termRunstatus,
            termDocHits: term.termDocHits,
          })
        );
      }
      setSelectedTermIds(selectedIds);
      onTermsSelection([...selectedIds.keys()]);
      gridAPIRef.current.setRowSelectionModel([...selectedIds.keys()]); // Select all
    };

    function getCheckboxInputProps(
      isAllSelected: boolean,
      isAnySelected: boolean
    ): React.InputHTMLAttributes<HTMLInputElement> {
      const isIndeterminate = !isAllSelected && isAnySelected;
      const ariaLabel = isIndeterminate
        ? t(stringPath + 'someTerms')
        : t(stringPath + 'selectAllTerms');
      const ariaChecked: boolean | 'mixed' = isIndeterminate
        ? 'mixed'
        : isAllSelected;

      return {
        'aria-label': ariaLabel,
        'aria-checked': ariaChecked,
      };
    }

    return (
      <>
        <span className="id-label-span">#</span>
        <AurCheckbox
          checked={isAllVisibleTermSelected}
          indeterminate={!isAllVisibleTermSelected && isAnyVisibleTermSelected}
          onChange={handleSelectAll}
          data-testid="select-all-checkbox"
          inputProps={getCheckboxInputProps(
            isAllVisibleTermSelected,
            isAnyVisibleTermSelected
          )}
        />
        <div className="sepeartor-line" />
      </>
    );
  };

  const renderIdCell = (params: GridRenderCellParams) => {
    const {
      id,
      row: {termId, termRunstatus, termDocHits},
    } = params;

    const {paginationConfig} = filterPaginationConfig;
    const rowIndex = params.api.getAllRowIds().indexOf(params.id);
    const isChecked = selectedTermIds.has(termId);
    return (
      <>
        <span className="id-label-span">
          {rowIndex +
            1 +
            paginationConfig.itemPerPage * (paginationConfig.currentPage - 1)}
        </span>
        <AurCheckbox
          checked={isChecked}
          onChange={() =>
            handleCheckboxCellClick(
              id as string,
              termRunstatus,
              termDocHits as number
            )
          }
          data-testid={`term-checkbox-${termId}`}
          inputProps={{
            'aria-label': t(stringPath + 'selectTerm'),
            'aria-checked': isChecked,
          }}
        />
      </>
    );
  };

  const renderTermCell = (params: GridRenderCellParams) => {
    const {
      value,
      row: {highlightColor},
    } = params;

    const colorObject = colorArray.find(
      (item) => item.backgroundColor === highlightColor
    );

    return (
      <span
        className="term-label-text str-term-cell-ellipsis"
        style={{
          backgroundColor: highlightColor,
          color: colorObject?.textColor ?? DEFAULT_TEXT_COLOR,
        }}
        id="str-result-term-text"
        tabIndex={0}
      >
        {unwrapQueryText(value)}
      </span>
    );
  };

  const renderHighLightCell = (params: GridRenderCellParams) => {
    const {value} = params;

    const colorObject = colorArray.find(
      (item) => item.backgroundColor === value
    );

    return (
      <span
        className="term-label-text high-light-label"
        style={{
          backgroundColor: value,
          color: colorObject?.textColor ?? DEFAULT_TEXT_COLOR,
        }}
        tabIndex={0}
      >
        {' A '}
      </span>
    );
  };

  const renderStatusCell = (params: GridRenderCellParams) => {
    const {value} = params;
    return (
      <span className="str-term-cell-ellipsis" tabIndex={0}>
        {t(getSTRStatusString(value))}
      </span>
    );
  };

  const renderLeftPanelForPagination = (): ReactElement => {
    return (
      <Grid
        container
        spacing={1.5}
        className="pagination-left-button-container"
      >
        <Grid item>
          <Typography className="typo-text body3_size">{`(${selectedTermIds.size})`}</Typography>
        </Grid>
        {hasDocumentPreviewPerm ? (
          <STRIconButton
            toolTipTitle={t(toolTipStringPath + 'docView')}
            iconName={IconNames.View}
            iconColor={IconColors.Custom}
            disabled={disabledDocButton}
            onClick={() => {
              handleViewDocViewerClick();
              trackIconClick(
                t('gaTracking.str.docView'),
                t('gaTracking.str.viewDocAction')
              );
            }}
            dataTestId="doc-view-button"
            customClassName="doc-view-icon"
          />
        ) : null}
        {hasSearchesPerm && hasTaggingDocumentPerm && (
          <STRIconButton
            toolTipTitle={t(toolTipStringPath + 'tagging')}
            iconName={IconNames.Tag}
            iconColor={IconColors.Custom}
            disabled={disabledTaggingButton}
            onClick={() => {
              handleTagginClick();
              trackIconClick(
                t('gaTracking.str.tagging'),
                t('gaTracking.str.taggingAction')
              );
              trackGAEvent({
                eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
                subPage: {
                  subpage_name: t('gaTracking.str.bulkTagging'),
                  subpage_type: GA_SUBPAGE_TYPE.MODAL,
                },
              });
            }}
            dataTestId={'tagging-button'}
            customClassName="doc-view-icon"
          />
        )}
        <Grid item>
          <span className="separator-button" />
        </Grid>
        <STRIconButton
          toolTipTitle={t(toolTipStringPath + 'add')}
          iconName={IconNames.Add}
          iconColor={IconColors.Primary}
          disabled={status === STRStatusType.InProgress}
          onClick={() => {
            handleAddMoreTermClick();
            trackIconClick(
              t('gaTracking.str.add'),
              window.location.host +
                navConstants.str.baseSTR +
                `?Flow=${STR_FLOW.ADDTERMS}`
            );
          }}
          dataTestId="add-term-button"
        />
        <STRIconButton
          toolTipTitle={t(toolTipStringPath + 'remove')}
          iconName={IconNames.Remove}
          iconColor={IconColors.Primary}
          disabled={
            status === STRStatusType.InProgress || selectedTermIds.size === 0
          }
          onClick={() => {
            setConfirmDeleteDialogVisible(true);
            trackIconClick(
              t('gaTracking.str.remove'),
              t('gaTracking.str.deleteTermAction'),
              true
            );
          }}
          dataTestId="remove-term-button"
        />
      </Grid>
    );
  };

  const renderDocHitsWithRGF = () => {
    return (
      <div className="sort-img-header-div aur-datagrid-search-box-column-separator">
        <span className="unique-hit-header-title">
          {t(stringPath + 'docHitWithRGFHits')}
        </span>
        <AurTooltip
          title={t(stringPath + 'docHitWithRGFHitsInfoMsg')}
          aria-label={t(stringPath + 'docHitWithRGFHitsInfoMsg')}
          className="typo-text body4_size unique-hit-tooltip-content"
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 0],
                  },
                },
              ],
            },
          }}
          disableInteractive
          disableFocusListener
        >
          <InfoIcon width={24} height={24} />
        </AurTooltip>
      </div>
    );
  };

  const renderDocNumberCell = (params: GridRenderCellParams) => {
    const {value, row} = params;
    let valueToDisplay = '--';
    if (
      getParseSTRStatus(row.termRunstatus) !== STRStatusType.Pending &&
      (value || value === 0)
    )
      valueToDisplay = formateNumber(value);
    return (
      <span className="doc-number-label str-term-cell-ellipsis" tabIndex={0}>
        {valueToDisplay}
      </span>
    );
  };

  const renderDateTimeCell = (params: GridRenderCellParams) => {
    const {value, field, row} = params;
    return (
      <span className="str-term-cell-ellipsis" tabIndex={0}>
        {row[field] ? getUTCDateTimeForDisplay(value) : '--'}
      </span>
    );
  };

  const renderUniqueHitHeader = () => {
    return (
      <div className="sort-img-header-div aur-datagrid-search-box-column-separator">
        <span className="unique-hit-header-title">
          {t(stringPath + 'uniqueHits')}
        </span>
        <AurTooltip
          title={t(stringPath + 'uniqueHitsInfoMsg')}
          aria-label={t(stringPath + 'uniqueHitsInfoMsg')}
          className="typo-text body4_size unique-hit-tooltip-content"
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 0],
                  },
                },
              ],
            },
          }}
          disableInteractive
          disableFocusListener
        >
          <InfoIcon width={24} height={24} />
        </AurTooltip>
      </div>
    );
  };

  const columns: CustomGridColDef[] = [
    {
      field: 'index',
      sortable: false,
      filterable: false,
      resizable: false,
      minWidth: 80,
      renderCell: renderIdCell,
      renderHeader: renderIdHeader,
      disableTooltip: true,
      cellClassName: 'index-checkox-cell',
      headerClassName: 'index-checkbox-header-cell',
      renderHeaderFilter: () => (
        <span className="sr-only">{t('common.noFilter')}</span>
      ),
    },
    {
      field: 'term',
      headerName: t(stringPath + 'terms'),
      sortable: true,
      resizable: false,
      width: 243,
      renderCell: renderTermCell,
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
        return (
          <FilterTextField
            gridRenderHeaderFilterProps={params}
            placeholder={t('fieldManagement.fieldTable.search')}
            parentClass="filter-input-field-container"
            data-testid="term-input-filter"
          />
        );
      },
    },
    {
      field: 'highlightColor',
      headerName: t(stringPath + 'highlighting'),
      sortable: false,
      filterable: false,
      resizable: false,
      width: 120,
      disableTooltip: true,
      renderCell: renderHighLightCell,
      renderHeaderFilter: () => (
        <span className="sr-only">{t('common.noFilter')}</span>
      ),
    },
    {
      field: 'termRunstatus',
      headerName: t(stringPath + 'status'),
      sortable: false,
      resizable: false,
      width: 221,
      renderCell: renderStatusCell,
      renderHeaderFilter: () => (
        <FieldPopoverGridFilter
          key={'termRunstatus'}
          columnField="termRunstatus"
          type="options"
          dataArray={termStatusList}
          apiRef={gridAPIRef}
          inputClass="filter-input-field-container"
          inputType="text"
          caretIconContainerClass="caret-dropdown-icon"
          dataTestId="term-run-status-input"
        />
      ),
    },
    {
      field: 'termDocHits',
      headerName: t(stringPath + 'docWithHits'),
      sortable: false,
      filterable: false,
      resizable: false,
      width: 150,
      renderCell: renderDocNumberCell,
      cellClassName: (params) => {
        let cssClass = '';
        if (
          getParseSTRStatus(params.row.termRunstatus) !== STRStatusType.Failed
        )
          cssClass = 'surface-dim-background';
        if (selectedTermIds.has(params.row.termId)) cssClass = '';
        return cssClass;
      },
      headerClassName: 'surface-moderate-background',
      renderHeaderFilter: () => (
        <span className="sr-only">{t('common.noFilter')}</span>
      ),
    },
    {
      field: 'termDocHitsWithRGF',
      headerName: t(stringPath + 'docHitWithRGFHits'),
      sortable: false,
      filterable: false,
      resizable: false,
      width: 320,
      renderCell: renderDocNumberCell,
      cellClassName: (params) => {
        let cssClass = '';
        if (
          getParseSTRStatus(params.row.termRunstatus) !== STRStatusType.Failed
        )
          cssClass = 'surface-dim-background';
        if (selectedTermIds.has(params.row.termId)) cssClass = '';
        return cssClass;
      },
      headerClassName: 'surface-moderate-background',
      renderHeader: renderDocHitsWithRGF,
      renderHeaderFilter: () => (
        <span className="sr-only">{t('common.noFilter')}</span>
      ),
    },
    {
      field: 'termUniqueHits',
      sortable: false,
      filterable: false,
      resizable: false,
      width: 150,
      renderCell: renderDocNumberCell,
      renderHeader: renderUniqueHitHeader,
      renderHeaderFilter: () => (
        <span className="sr-only">{t('common.noFilter')}</span>
      ),
    },
    {
      field: 'termUniqueHitsWithRGF',
      headerName: t(stringPath + 'uniqueHitPlusRGF'),
      sortable: false,
      filterable: false,
      resizable: false,
      width: 260,
      renderCell: renderDocNumberCell,
      renderHeaderFilter: () => (
        <span className="sr-only">{t('common.noFilter')}</span>
      ),
    },
    {
      field: 'termLastRunTime',
      headerName: t(stringPath + 'lastRunTime'),
      sortable: true,
      resizable: false,
      width: 221,
      renderCell: renderDateTimeCell,
      type: 'date',
      valueGetter: (params) => new Date(params),
      isDateColumn: true,
      renderHeaderFilter: () => (
        <FieldPopoverGridFilter
          apiRef={gridAPIRef}
          columnField={'termLastRunTime'}
          type={'date'}
          inputClass="filter-input-field-container"
          inputType="text"
          caretIconContainerClass="caret-dropdown-icon"
          applyNotSetToFilter
          excludeDateFilterConditions={[]}
          dataTestId="term-last-run-time-input"
        />
      ),
    },
    {
      field: 'termLastUpdatedTime',
      headerName: t(stringPath + 'lastUpdated'),
      sortable: true,
      resizable: false,
      width: 221,
      renderCell: renderDateTimeCell,
      type: 'date',
      isDateColumn: true,
      valueGetter: (params) => new Date(params),
      renderHeaderFilter: () => (
        <FieldPopoverGridFilter
          apiRef={gridAPIRef}
          columnField={'termLastUpdatedTime'}
          type={'date'}
          inputClass="filter-input-field-container"
          inputType="text"
          caretIconContainerClass="caret-dropdown-icon"
          applyNotSetToFilter
          excludeDateFilterConditions={[]}
          dataTestId="term-last-updated-time-input"
        />
      ),
    },
  ];

  const perPageOptions = useMemo(() => {
    const options = TABLE_PAGE_SIZE_OPTION.filter(
      (option) => option <= totalTerms
    );
    const additionalOption = TABLE_PAGE_SIZE_OPTION.find(
      (option) => option >= totalTerms
    );
    if (additionalOption && !options.includes(additionalOption)) {
      options.push(additionalOption);
    }
    return options;
  }, [totalTerms]);

  const trackIconClick = (name: string, action: string, isDelete = false) => {
    trackGAEvent({
      eventName: isDelete ? GA_EVENT_NAMES.DELETE : GA_EVENT_NAMES.CLICK,
      click: {
        name: name,
        type: GA_CLICK_TYPES.ICON,
        region: GA_REGIONS.BODY,
        action: action,
        details: t('gaTracking.str.selectedTermsAction', {
          termName: isAllVisibleTermSelected
            ? t('gaTracking.str.selectedTerms.all')
            : selectedTermIds.size > 1
              ? t('gaTracking.str.selectedTerms.multiple')
              : t('gaTracking.str.selectedTerms.one'),
        }),
      },
    });
  };

  return (
    <>
      <div className="search-term-table-container" ref={scrollRef}>
        <AurPagination
          totalItems={totalTerms}
          itemsPerPage={filterPaginationConfig.paginationConfig.itemPerPage}
          currentPage={filterPaginationConfig.paginationConfig.currentPage}
          perPageOptions={perPageOptions}
          setItemsPerPage={handleItemsPerPageChange}
          onPageChange={handleCurrenPageChange}
          itemType={t('common.reports')}
          leftSidePageCaptionVisible={false}
          renderLeftComponent={renderLeftPanelForPagination}
          className="pagination-panel"
          onClearFilterClick={() => {
            gridAPIRef.current?.setFilterModel({items: []});
          }}
          appliedFilterCount={filterPaginationConfig.filter?.items?.length ?? 0}
        />
        <AurDataGridPro
          localeText={customLocaleText}
          apiRef={gridAPIRef}
          rows={filteredTerms}
          headerFilters
          enableServerSide
          visibleToolTip
          columns={columns}
          getRowId={(row) => row.termId}
          selectedIds={[...selectedTermIds.keys()]}
          rowHeight={36}
          columnHeaderHeight={48}
          headerFilterHeight={50}
          hideFooter
          className="aur-data-grid-pro-filter"
          onRowClick={handleTermRowClick}
          checkboxSelection={false}
          getRowClassName={(params) =>
            getParseSTRStatus(params.row.termRunstatus) === STRStatusType.Failed
              ? 'error-state-row'
              : ''
          }
          showPagination={false}
          defaultItemsPerPage={TABLE_PER_PAGE_DEFAULT_NO_ROW}
          haveReqError={haveError}
          columnVisibilityModel={{
            termUniqueHits: isIncludeUniqueHitsFlagColumnVisible,
            termUniqueHitsWithRGF: isTermUniqueHitsWithRGFColumnVisible,
            termDocHitsWithRGF: isDocsHitsWithRGFColumnVisible,
          }}
          onFilterModelChange={handleFilterChangeEvent}
          sortModel={filterPaginationConfig.sort}
          setSortModel={handleSortingChangeEvent}
          onErrorRefresh={onErrorRefresh}
          filterMode="server"
          onRowSelectionModelChange={(selection: GridRowSelectionModel) => {
            // when selection is zero then reset selected terms ids as no row selected currently
            if (selection.length === 0) setSelectedTermIds(new Map());
          }}
          currentPage={filterPaginationConfig.paginationConfig.currentPage}
          itemsPerPage={filterPaginationConfig.paginationConfig.itemPerPage}
        />
      </div>
      <AurDialog
        showDialog={confirmDeleteDialogVisible}
        type="alert"
        popupTitle={t(stringPath + 'deleteConfirmTerm.title')}
        onOk={handleConfirmDeleteTermClick}
        onCancel={setConfirmDeleteDialogVisible.bind(this, false)}
        labelActionOk={t('common.yes')}
        labelActionCancel={t('common.cancel')}
        okLabelClassName="yes-button"
        dismissable={false}
        infoIcon={
          <div className="delete-confirm-alert-icon">
            <InfoAlertIcon />
          </div>
        }
        className="with-max-width"
        popupContent={
          <>
            <span>{t(stringPath + 'deleteConfirmTerm.msgTop')}</span>
            <br />
            <br />
            <span className="selected-term-label">
              {t(stringPath + 'deleteConfirmTerm.selectedTerm', {
                count: selectedTermIds.size,
              })}
            </span>
            <br />
            <br />
            <span>{t(stringPath + 'deleteConfirmTerm.msgBottom')}</span>
          </>
        }
      />
      {ecaDocumentViewer ? (
        <div data-testId="document-viewer">
          <EcaDocumentContainer
            selectedDocumentIds={selectionDocuments.current}
            selectedDocLink={selectedDocLink}
            docsPerPage={itemsPerPage}
            docsCurrentPage={startIndex}
            scrollFetching={scrollFetching}
            filterSet={filterSet}
            reportId={reportId}
            projectCode={projectCode}
            selectedTermIds={[...selectedTermIds.keys()]}
            onDocTextLoad={fetchHighlightedDocText}
            stdIndexFieldTemplateId={Number(fieldTemplateId)}
          />
        </div>
      ) : null}
      {showSearchLoader && (
        <AurEcaSearchLoader
          isOpen={showSearchLoader}
          onClose={handleLoaderClose}
          keepMounted
        />
      )}
    </>
  );
};

export default STRSearchTermTable;
